import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  StickyNote,
  Car,
  List,
  LayoutGrid,
  LayoutList,
  Plane,
  Plus,
  MoreVertical,
  Bell,
  Users,
  Mail,
  Shield,
  RefreshCw,
  Settings,
  Pencil,
  X,
  FileDown,
  CheckSquare,
  Square
} from 'lucide-react';
import { resolveTripBackTarget } from '../../utils/tripBackTarget';

const TripCompactHeader = ({
  trip,
  viewMode,
  onViewModeChange,
  onAddNote,
  onUrbanrideClick,
  urbanrideSelectionMode,
  onAddComponent,
  onEditTrip,
  onManagePermissions,
  onManageExecutives,
  onManageEmail,
  eaEmailsLocked = false,
  onRefresh,
  pendingTodosCount = 0,
  hasOverdueTodos = false,
  onTodosClick,
  isRefreshing = false,
  // PDF Export props
  pdfExportMode = false,
  onPdfExportClick,
  onPdfExportCancel,
  onPdfExportGenerate,
  onPdfSelectAll,
  pdfSelectedCount = 0,
  pdfTotalCount = 0,
  pdfFormat = 'executive',
  onPdfFormatChange,
  isSpeakerBureau = false,
  // View-only mode (speaker portal): hides every mutating action, keeps
  // view toggles and PDF export
  readOnly = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Return to whichever page linked here (the executive dashboard, the trips
  // list, ...), falling back to the trips list when that is unknown.
  const back = resolveTripBackTarget(location.state);

  const handleBack = () => {
    navigate(back.to);
  };

  return (
    <div className="sticky top-0 z-50 shadow-sm border-b bg-ui-surface border-ui-line">
      <div className="px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Left: Back + Trip Title */}
          <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-shrink">
            <button
              onClick={handleBack}
              className="p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0 hover:bg-ui-hover text-ui-ink-3 hover:text-ui-ink"
              title={back.label}
              aria-label={back.label}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base sm:text-lg font-semibold truncate max-w-[120px] sm:max-w-[200px] md:max-w-[300px] text-ui-ink">
              {trip?.title || 'Trip'}
            </h1>
          </div>

          {/* Center: Timeline Controls */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Normal mode controls */}
            {!pdfExportMode && (
              <>
                {/* Add Note Button - only show when there are components */}
                {!urbanrideSelectionMode && pdfTotalCount > 0 && !readOnly && (
                  <button
                    onClick={onAddNote}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-sm font-medium rounded-lg transition-colors text-ui-amber-fg bg-ui-amber-bg hover:bg-ui-amber-line border border-ui-amber-line"
                    title="Add sticky note"
                  >
                    <StickyNote className="w-4 h-4" />
                    <span className="hidden sm:inline">Note</span>
                  </button>
                )}

                {/* PDF Export Button - only show when there are components */}
                {!urbanrideSelectionMode && pdfTotalCount > 0 && (
                  <button
                    onClick={onPdfExportClick}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-sm font-medium rounded-lg transition-colors text-ui-violet-fg bg-ui-violet-bg hover:bg-ui-violet-line border border-ui-violet-line"
                    title="Export timeline as PDF"
                  >
                    <FileDown className="w-4 h-4" />
                    <span className="hidden sm:inline">PDF</span>
                  </button>
                )}

                {/* Urbanride Button */}
                {!readOnly && (
                <button
                  onClick={onUrbanrideClick}
                  className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    urbanrideSelectionMode
                      ? 'bg-ui-primary text-ui-on-primary hover:bg-ui-primary-hover'
                      : 'text-ui-ink-2 bg-ui-hover hover:bg-ui-line border border-ui-line'
                  }`}
                  title={urbanrideSelectionMode ? 'Cancel Urbanride selection' : 'Book Urbanride'}
                >
                  <Car className="w-4 h-4" />
                  <span className="hidden sm:inline">Ride</span>
                  {urbanrideSelectionMode && <X className="w-3 h-3 ml-1" />}
                </button>
                )}

                {/* View Toggle */}
                {!urbanrideSelectionMode && (
                  <div className="flex items-center rounded-lg p-0.5 bg-ui-hover">
                    <button
                      onClick={() => onViewModeChange('summary')}
                      className={`p-1.5 rounded-md transition-colors ${
                        viewMode === 'summary'
                          ? 'bg-ui-line text-ui-ink shadow-sm'
                          : 'text-ui-ink-3 hover:text-ui-ink'
                      }`}
                      title="Summary view"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onViewModeChange('compact')}
                      className={`p-1.5 rounded-md transition-colors ${
                        viewMode === 'compact'
                          ? 'bg-ui-line text-ui-ink shadow-sm'
                          : 'text-ui-ink-3 hover:text-ui-ink'
                      }`}
                      title="Compact view"
                    >
                      <LayoutList className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onViewModeChange('cards')}
                      className={`p-1.5 rounded-md transition-colors ${
                        viewMode === 'cards'
                          ? 'bg-ui-line text-ui-ink shadow-sm'
                          : 'text-ui-ink-3 hover:text-ui-ink'
                      }`}
                      title="Cards view — flight legs rendered like the executive dashboard timeline"
                    >
                      <Plane className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onViewModeChange('full')}
                      className={`p-1.5 rounded-md transition-colors ${
                        viewMode === 'full'
                          ? 'bg-ui-line text-ui-ink shadow-sm'
                          : 'text-ui-ink-3 hover:text-ui-ink'
                      }`}
                      title="Full view"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </>
            )}

            {/* PDF Export Mode Controls */}
            {pdfExportMode && (
              <>
                {/* Format Selector */}
                <select
                  value={pdfFormat}
                  onChange={(e) => onPdfFormatChange(e.target.value)}
                  className="px-2 sm:px-3 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-ui-violet-solid text-ui-ink-2 bg-ui-hover border border-ui-line hover:bg-ui-line"
                  title="Select PDF format"
                >
                  {isSpeakerBureau ? (
                    <option value="speaker">Speaker</option>
                  ) : (
                    <option value="executive">Executive</option>
                  )}
                  <option value="simplified">Simplified</option>
                  <option value="simple">Simple List</option>
                </select>
                <button
                  onClick={onPdfSelectAll}
                  className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-sm font-medium rounded-lg transition-colors text-ui-ink-2 bg-ui-hover hover:bg-ui-line border border-ui-line"
                  title={pdfSelectedCount === pdfTotalCount ? 'Deselect all' : 'Select all'}
                >
                  {pdfSelectedCount === pdfTotalCount ? (
                    <Square className="w-4 h-4" />
                  ) : (
                    <CheckSquare className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">{pdfSelectedCount === pdfTotalCount ? 'None' : 'All'}</span>
                </button>
                <button
                  onClick={onPdfExportGenerate}
                  disabled={pdfSelectedCount === 0}
                  className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    pdfSelectedCount > 0
                      ? 'text-ui-on-primary bg-ui-violet-strong hover:bg-ui-violet-strong/90'
                      : 'text-ui-muted bg-ui-hover cursor-not-allowed'
                  }`}
                  title="Export selected items to PDF"
                >
                  <FileDown className="w-4 h-4" />
                  <span className="hidden sm:inline">Export</span>
                  <span className="text-xs">({pdfSelectedCount})</span>
                </button>
                <button
                  onClick={onPdfExportCancel}
                  className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-sm font-medium rounded-lg transition-colors text-ui-ink-2 bg-ui-hover hover:bg-ui-line border border-ui-line"
                  title="Cancel PDF export"
                >
                  <X className="w-4 h-4" />
                  <span className="hidden sm:inline">Cancel</span>
                </button>
              </>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Add Component Button */}
            {!readOnly && (
            <button
              onClick={onAddComponent}
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-sm font-medium text-ui-on-primary bg-ui-primary hover:bg-ui-primary-hover rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add</span>
            </button>
            )}

            {/* More Menu */}
            <div className="relative" ref={moreMenuRef}>
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className="p-1.5 sm:p-2 rounded-lg transition-colors hover:bg-ui-hover text-ui-ink-3 hover:text-ui-ink"
                title="More options"
              >
                <MoreVertical className="w-5 h-5" />
              </button>

              {showMoreMenu && (
                <div className="absolute right-0 mt-1 w-52 rounded-lg shadow-ui-pop py-1 z-50 bg-ui-raised border border-ui-line">
                  {!readOnly && (
                  <>
                  <button
                    onClick={() => { onEditTrip(); setShowMoreMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-ui-ink-2 hover:bg-ui-hover hover:text-ui-ink"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit Trip Details
                  </button>
                  <button
                    onClick={() => { onManagePermissions(); setShowMoreMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-ui-ink-2 hover:bg-ui-hover hover:text-ui-ink"
                  >
                    <Shield className="w-4 h-4" />
                    Manage Permissions
                  </button>
                  <button
                    onClick={() => { onManageExecutives(); setShowMoreMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-ui-ink-2 hover:bg-ui-hover hover:text-ui-ink"
                  >
                    <Users className="w-4 h-4" />
                    Manage Executives
                  </button>
                  {!eaEmailsLocked && (
                    <button
                      onClick={() => { onManageEmail(); setShowMoreMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-ui-ink-2 hover:bg-ui-hover hover:text-ui-ink"
                    >
                      <Mail className="w-4 h-4" />
                      Email Addresses
                    </button>
                  )}
                  <div className="my-1 border-t border-ui-divider" />
                  </>
                  )}
                  <button
                    onClick={() => { onRefresh(); setShowMoreMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-ui-ink-2 hover:bg-ui-hover hover:text-ui-ink"
                  >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              )}
            </div>

            {/* Todos Badge Button */}
            {!readOnly && (
            <button
              onClick={onTodosClick}
              className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg transition-all ${
                hasOverdueTodos
                  ? 'bg-ui-red-bg text-ui-red-fg border border-ui-red-line hover:bg-ui-red-line'
                  : pendingTodosCount > 0
                    ? 'bg-ui-orange-bg text-ui-orange-fg border border-ui-orange-line hover:bg-ui-orange-line'
                    : 'bg-ui-hover text-ui-ink-3 border border-ui-line hover:bg-ui-line hover:text-ui-ink'
              }`}
              title={`${pendingTodosCount} pending todos`}
            >
              <div className="relative">
                <Bell className="w-4 h-4" />
                {hasOverdueTodos && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-ui-red-solid rounded-full animate-pulse" />
                )}
              </div>
              {pendingTodosCount > 0 && (
                <span className="text-sm font-medium">
                  {pendingTodosCount}
                  {hasOverdueTodos && <span className="hidden sm:inline">!</span>}
                </span>
              )}
            </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripCompactHeader;
